// @ts-strict
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { LMLoggerService } from './lm-logger.service';
import { DrillResource } from '@resources/drill.resource';
import { LMUrlUtilService } from './utils';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LMFormRequest } from '@form/index';
import { FormRequestType } from '@app/utils';
import * as urltemplate from 'url-template';

@Injectable()
export class LMDrillService {
  constructor(
    private http: HttpClient,
    private _logger: LMLoggerService,
    private urlUtilService: LMUrlUtilService,
    private lmLoggerService: LMLoggerService) {
  }

  public getDrillPoints(drillPointsParams: DrillPointsParams): Observable<LMDrillResponse> {
    if (drillPointsParams.dataView === undefined) {
      return of({ drillPoints: [] });
    }

    const drillResource = new DrillResource({ resourceId: drillPointsParams.dataView.resourceId, drillName: '_drillpoints_' });
    let url = this.urlUtilService.buildUrl({ resource: drillResource });

    if (drillPointsParams.drillAround) {
      url = this.urlUtilService.addParamToUrl(url, 'drill-around', 'true');   // this for sumo logging
    }

    const headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json;charset=utf-8');

    return this.http.get<LMDrillResponse>(url, { headers }).pipe(
      catchError(error => this._handleError(error)));
  }

  // this is called by the standalone drill widget
  public getDrillAroundPoints(url: string): Observable<LMDrillResponse> {
    url = this.urlUtilService.addParamToUrl(url, 'drill-around', 'true');   // this for sumo logging

    const headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json;charset=utf-8');

    return this.http.get<LMDrillResponse>(url, { headers }).pipe(
      catchError(error => this._handleError(error)));
  }

  public getFieldDrillPoints(drillPointsParams: DrillPointsParams): Observable<LMDrillResponse> {
    if (!drillPointsParams.formRequestUrl || !drillPointsParams.dataView) {
      return of({});
    }

    const compiledFormRequestUrl = urltemplate.parse(drillPointsParams.formRequestUrl);
    const url = compiledFormRequestUrl.expand({ resourceId: drillPointsParams.dataView.resourceId });

    const formRequest: LMFormRequest = {
      requestType: FormRequestType.DrillPoints,
      dataView:    drillPointsParams.dataView,
      drillName:   '_drillPoints_',
      fieldId:     drillPointsParams.fieldId
    };

    const headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json;charset=utf-8');

    return <Observable<LMDrillResponse>> this.http.post(url, JSON.stringify(formRequest), { headers }).pipe(
      catchError(error => this._handleError(error)));
  }

  private _handleError(error: Response): Observable<never> {
    // in a real world app, we may send the error to some remote logging infrastructure
    // instead of just logging it to the console
    this.lmLoggerService.log(error);
    return throwError(() => error || 'Server error');
  }
}

export interface DrillPointsParams {
  dataView?: LMDataView;
  fieldId?:  string;
  formRequestUrl?: string;
  drillAround?: boolean;
}
